
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import Axios from 'axios'
import { dataLayer, ga4Token } from '../core/constants'
import { ANTIFORGERY_INPUT_NAME, BASE_URL, TIMEOUT_AMOUNT_ROUND_OFF_MESSAGE } from '../helpers/constants'
import { onKeyDown } from '../helpers/functions'

@Component({
  template: `
    <div
      class="flex flex-grow-1 flex-align-end add-to-cart-button"
    >
      <div class="flex flex-grow-1 field-set flex-align-end flex-justify-end" :class="{error: modelAmount === 0 || modelAmount === ''}">
        <input
          v-tooltip.left="{ content: inputRoundOffMessage, show: inputRoundOffMessage && showInputMessage, trigger: 'manual' }"
          v-model.number="modelAmount"
          @input='$emit("update:amount", amount)'
          class="full-width add-to-cart-input"
          name="amount"
          type="number"
          :placeholder="placeholder"
          @focus='showInputMessage = false'
          @keyup.enter="addToCart"
          @blur="onBlur"
          @keydown="functionOnKeyDown"
          :autofocus="focus"
        />
        <div class="label">{{ label }}</div>
      </div>
      <button @click="addToCart" :disabled="loading" :loading="loading" class="button">
        <i class="icons8-shopping-cart"></i>
        <span v-if="buttonText" class="ml-05">{{ buttonText }}</span>
      </button>
    </div>
  `
})
export default class AddToCartButton extends Vue {
  @Prop({ default: '' }) label: string
  @Prop({ default: '' }) buttonText: boolean
  @Prop({ default: false }) focus: boolean
  @Prop({ default: '' }) productId: string
  @Prop({ default: '' }) sku: string
  @Prop({ default: 1 }) salesUnit: number
  @Prop({ default: null }) amount: number
  @Prop({ default: '' }) placeholder: string
  @Prop({ default: '' }) measureUnit: string

  loading = false

  functionOnKeyDown = onKeyDown

  inputRoundOffMessage = this.$options?.filters?.t('inputRoundOffMessage')

  showInputMessage = false

  modelAmount: any = null

  salesUnitAsNumber = 1

  timeoutId: any = null

  model: any = { productId: '', quantityDecimal: 0, measureUnit: '', sku: '' }

  // only check if amount complies with salesUnit and show tooltip, dont adjust amount
  get validAmount(): boolean {
    return !this.modelAmount || (!!this.modelAmount && this.modelAmount > 0 && this.modelAmount % this.salesUnitAsNumber === 0)
  }

  async addToCart(): Promise<void> {
    this.correctAmount()
    const antiForgeryInput: HTMLInputElement | null = document.querySelector(`input[name=${ANTIFORGERY_INPUT_NAME}]`)
    this.model[ANTIFORGERY_INPUT_NAME] = antiForgeryInput?.value

    this.model.productId = this.productId
    this.model.quantityDecimal = this.modelAmount
    this.model.measureUnit = this.measureUnit
    this.model.sku = this.sku

    // don't send request when quantity is 0
    if(this.model.quantityDecimal !== 0 && this.model.quantityDecimal !== ''){
      this.loading = true

      try {
        const res = await Axios({
          method: 'post',
          url: `${BASE_URL}/cart/items`,
          data: this.model
        })
        if (res.status === 200) {
          this.$store.dispatch('cart/getShoppingCartCount')
          this.sendGaAddToCartEvent(this.model)

          this.$store.commit('notifications/add', {
            message: this.$options?.filters?.t('itemAddedToCart'),
            center: true,
            timeout: 20000,
            continueShopping: this.$options?.filters?.t('continueShoppingPopupLabel'),
            goToCart: this.$options?.filters?.t('toShoppingCartLabel')
          })
        } else throw new Error()
      } catch (error) {
        this.$store.commit('notifications/add', { message:  this.$options?.filters?.t('addToCartError'), center: true })
      } finally {
        this.loading = false
      }
    }
  }

  sendGaAddToCartEvent(product: any){
    if (!dataLayer) return

    const quantity = typeof product.quantityDecimal === 'string' ? +(product.quantityDecimal.replace(",", ".")) : product.quantityDecimal
    dataLayer.push({ ecommerce: null });  // Clear the previous ecommerce object.
    /* eslint-disable @typescript-eslint/camelcase */
    dataLayer.push({
      event: 'addToCart',
      ecommerce: {
        items: [{
          item_name: product.sku,
          item_id: product.productId,
          quantity: quantity
        }],
        add: {
          products: [{
            name: product.sku,
            id: product.productId,
            quantity: quantity
          }]
        }
      }
    });
  }

  correctAmount() {
    if (this.validAmount) return

    const modulo = this.modelAmount % this.salesUnitAsNumber
    const amount = modulo === 0 ? this.modelAmount : this.modelAmount - modulo + this.salesUnitAsNumber

    this.modelAmount = amount

    this.showInputMessage = true
    this.handleTimeoutForInputMessage()
  }

  handleTimeoutForInputMessage() {
    // stop timeout when one is in progress
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }

    this.timeoutId = setTimeout(() => {
      this.showInputMessage = false
      this.timeoutId = null
    }, TIMEOUT_AMOUNT_ROUND_OFF_MESSAGE);
  }

  onBlur(): void {
    this.correctAmount()
    this.$emit('blur', this.modelAmount)
  }

  created() {
    // first time setting the modelAmount depends on salesUnit. Set to 1 when it is 0.
    // salesUnit should be ints and have no values after dot. So first time getting the salesUnit, it will be 1 when it is 0 or less and when it is a decimal it will be round off to the next higher number
    this.$nextTick(() => {
      const salesUnitAsNumber = typeof this.salesUnit === 'string' ? +((this.salesUnit as string).replace(',','.')) : this.salesUnit
      const salesUnitInteger = Number.isInteger(salesUnitAsNumber)
      const customSalesUnit = salesUnitAsNumber <= 0 ? 1 : salesUnitAsNumber

      this.salesUnitAsNumber = customSalesUnit

      if(salesUnitInteger) this.modelAmount = customSalesUnit
      else this.modelAmount = salesUnitAsNumber <= 0 ? 1 : parseFloat(salesUnitAsNumber.toFixed(3))
    })
  }

  @Watch('amount', { immediate: true })
  onAmountChange(amount: number | string): void {
    // possibility that the amount is string, cast it to number
    if (typeof amount === 'string') amount = +(amount)

    this.modelAmount = amount
  }
}
